import { useEffect, useMemo, useState, useCallback, type KeyboardEvent } from "react";import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { Search, Trash2, ImageIcon, ChevronRight, ScrollText, Receipt, MoreVertical, Sparkles, Share2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDateShort } from "@/shared/format";

// JS interop imports — widen prop types once at the seam.
import ExplainEntryButtonJs from "@/components/ExplainEntryButton";
import { AttachmentBadges as AttachmentBadgesJs } from "@/components/AttachmentsPanel";
import VoicePlayerJs from "@/components/VoicePlayer";
import PageHeaderJs from "@/components/PageHeader";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { useShareToAccountant as useShareToAccountantJs } from "@/hooks/useShareToAccountant";

// JS interop — the hook is `.jsx` so TS can't infer its return shape via
// allowJs. The runtime contract is exactly `shareVia(kind, opts)`.
const useShareToAccountant = useShareToAccountantJs as unknown as () => {
  shareVia: (kind: string, opts: { text: string; subject?: string; pdfUrl?: string; filename?: string }) => Promise<void>;
};
import {
  DropdownMenu,
  DropdownMenuContent as DropdownMenuContentJs,
  DropdownMenuItem as DropdownMenuItemJs,
  DropdownMenuTrigger as DropdownMenuTriggerJs,
  DropdownMenuSeparator as DropdownMenuSeparatorJs,
} from "@/components/ui/dropdown-menu";

// Widen the JS Shadcn primitives at the seam — they're forwardRef'd and TS
// can't infer their prop shape via allowJs.
const DropdownMenuContent = DropdownMenuContentJs as unknown as React.FC<
  React.PropsWithChildren<{ align?: string; className?: string; "data-testid"?: string }>
>;
const DropdownMenuTrigger = DropdownMenuTriggerJs as unknown as React.FC<
  React.PropsWithChildren<{ asChild?: boolean }>
>;
const DropdownMenuItem = DropdownMenuItemJs as unknown as React.FC<
  React.PropsWithChildren<{
    onClick?: () => void | Promise<void>;
    onSelect?: (event: Event) => void;
    asChild?: boolean;
    className?: string;
    "data-testid"?: string;
  }>
>;
const DropdownMenuSeparator = DropdownMenuSeparatorJs as unknown as React.FC<{
  className?: string;
}>;

import { AtoDisclaimer } from "@/components/tax/AtoDisclaimer";
import RulesetStamp from "@/components/tax/RulesetStamp";
import { FlagPill } from "@/components/flags/SmartFlags";
import { formatMoney } from "@/shared";
import { useFileToken, buildPhotoHref } from "@/hooks/useFileToken";

const ExplainEntryButton = ExplainEntryButtonJs as unknown as React.FC<{
  entryId?: string;
  supplier?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}>;
const AttachmentBadges = AttachmentBadgesJs as unknown as React.FC<{ entry: unknown }>;
const VoicePlayer = VoicePlayerJs as unknown as React.FC<{
  voicePath: string;
  transcript?: string;
  testid?: string;
}>;
const PageHeader = PageHeaderJs as unknown as React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle?: string;
  testid?: string;
  right?: React.ReactNode;
  accent?: boolean;
}>;

/**
 * Ledger — decluttered per iter47.
 *
 * Structural changes vs the previous History.jsx:
 *   • Unified KPI card (Total · GST · Entries) with proper AU number formatting.
 *   • Strong AtoDisclaimer beneath the KPIs (tax_summary context).
 *   • Row cleaned to 3 scannable rows: header (supplier + amount) · description
 *     · meta+status+kebab. Explain and Delete moved into a Shadcn DropdownMenu.
 *   • Search + filter pills unchanged; counter added.
 *
 * Nothing was removed — see the DropdownMenu for the previously inline actions.
 */

interface Entry {
  id: string;
  supplier?: string;
  description?: string;
  notes?: string;
  total?: number;
  gst?: number;
  entry_date?: string;
  job_id?: string;
  status?: "pending" | "approved" | "rejected";
  photo_path?: string;
  voice_path?: string;
  raw_transcript?: string;
  // Multi-file uploads (via AttachmentsPanel) land here. The Ledger
  // falls back to the first image in this array when `photo_path` is
  // absent so evidence stays visible no matter which upload path the
  // tradie used.
  attachments?: Array<{
    id?: string;
    path?: string;
    filename?: string;
    content_type?: string;
    kind?: string;
  }>;
  // Server-set when the tradie explicitly saves / bulk-approves the
  // row — vouches for the category + GST. AI-suggested rows stay
  // undefined/false so the chip renders on silence-by-default rows.
  client_confirmed?: boolean;
  client_confirmed_at?: string;
}

const STATUSES = ["all", "pending", "approved", "rejected"] as const;
type StatusFilter = (typeof STATUSES)[number];

const statusClass = (s?: string): string => {
  if (s === "approved") return "text-[#34D399] border-[#34D399]/40 bg-[#34D399]/10";
  if (s === "rejected") return "text-[#F87171] border-[#F87171]/40 bg-[#F87171]/10";
  return "text-[#FBBF24] border-[#FBBF24]/40 bg-[#FBBF24]/10";
};

export default function History() {
  const { jobs } = useApp() as unknown as { jobs: Array<{ id: string; name: string }> };
  const nav = useNavigate();
  const [items, setItems] = useState<Entry[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");
  const [loading, setLoading] = useState(true);
  // ?flag=KEY deep-link filter — driven by the assistant's "Show these"
  // chips so the tradie lands on exactly the flagged rows. Kept in
  // URL state so back/forward + refresh preserve the filter.
  const [searchParams, setSearchParams] = useSearchParams();
  const flagFilter = searchParams.get("flag") || "";
  // Per-entry flag count map — populated below from /flags/summary. Kept
  // here (before `filtered`) so the memo can reference it without a TS
  // "used before declaration" error.
  const [flagsByEntry, setFlagsByEntry] = useState<Record<string, number>>({});
  const [entriesByFlagKey, setEntriesByFlagKey] = useState<Record<string, Set<string>>>({});
  // Sheet-state for the Explain dialog is lifted here (not owned by
  // `ExplainEntryButton`) so we can drive it from `DropdownMenuItem.onSelect`.
  // The nested `asChild` + inner-button pattern didn't work because Radix
  // consumes pointer events at the menu-item boundary.
  const [explainFor, setExplainFor] = useState<{
    entryId: string;
    supplier?: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<Entry[]>("/entries");
      setItems(data);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status !== 401) toast.error("Couldn't load ledger");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    // When ?flag=KEY is set but no entries carry that key, we must still
    // filter to zero rows (empty state). Falling back to `undefined` would
    // silently disable the filter and show the whole ledger — see iter81.
    const flaggedSet = flagFilter ? (entriesByFlagKey[flagFilter] ?? new Set<string>()) : null;
    return items.filter((e) => {
      if (status !== "all" && e.status !== status) return false;
      if (flaggedSet && !flaggedSet.has(e.id)) return false;
      const t = `${e.supplier ?? ""} ${e.description ?? ""} ${e.notes ?? ""}`.toLowerCase();
      return q ? t.includes(q.toLowerCase()) : true;
    });
  }, [items, q, status, flagFilter, entriesByFlagKey]);

  const total = filtered.reduce((a, b) => a + Number(b.total || 0), 0);
  const gst = filtered.reduce((a, b) => a + Number(b.gst || 0), 0);
  const jobName = (id?: string) => jobs.find((j) => j.id === id)?.name || "Unassigned";

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const { shareVia } = useShareToAccountant();
  const fileToken = useFileToken();

  // Per-entry flag count map — fetched once from the summary endpoint so
  // every row can render a small warning pill without hitting the API N times.
  // Also builds `entriesByFlagKey` for the ?flag=KEY deep-link filter.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get<{
          flags?: Array<{ entry_id?: string; key?: string }>;
          by_key?: Record<string, number>;
        }>("/flags/summary");
        if (cancelled) return;
        const map: Record<string, number> = {};
        const byKey: Record<string, Set<string>> = {};
        for (const f of data?.flags || []) {
          if (!f.entry_id) continue;
          map[f.entry_id] = (map[f.entry_id] || 0) + 1;
          if (f.key) {
            (byKey[f.key] ||= new Set<string>()).add(f.entry_id);
          }
        }
        setFlagsByEntry(map);
        setEntriesByFlagKey(byKey);
      } catch { /* silent — pills just won't render */ }
    })();
    return () => { cancelled = true; };
  }, [items.length]);

  /** Share a single expense (PDF summary + inlined receipt image[s]). */
  const shareReceipt = useCallback(async (e: Entry) => {
    // Human-friendly summary shown in the OS share sheet body / mailto body.
    const dateStr = e.entry_date ? formatDateShort(e.entry_date) : "";
    const money = `$${Number(e.total || 0).toFixed(2)}`;
    const text = `${e.supplier || "Expense"} — ${money}${dateStr ? ` on ${dateStr}` : ""}. Attached: BuildIntel receipt PDF with photo.`;
    const subject = `Expense receipt — ${e.supplier || "Untitled"} (${dateStr || "no date"})`;
    const slug = (e.supplier || "expense").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "expense";
    try {
      await shareVia("native", {
        text,
        subject,
        pdfUrl: `/entries/${e.id}/share-pdf`,
        filename: `buildintel-expense-${e.entry_date || "undated"}-${slug}.pdf`,
      });
    } catch (err) {
      // useShareToAccountant already toasts on failure. Swallow so a share
      // cancel doesn't leak into the console as an unhandled rejection.
      void err;
    }
  }, [shareVia]);

  const del = (id: string) => setPendingDelete(id);
  const confirmDelete = async () => {
    const id = pendingDelete;
    setPendingDelete(null);
    if (!id) return;
    try {
      await api.delete(`/entries/${id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  // When the tradie taps a flagged row from a `?flag=KEY` filtered view,
  // carry the flag into the entry editor as `?fix=KEY` so EntryEdit
  // can highlight the exact field that needs fixing (Fix-Now flow).
  const openEdit = (id: string) => {
    const target = flagFilter
      ? `/entries/${id}/edit?fix=${encodeURIComponent(flagFilter)}`
      : `/entries/${id}/edit`;
    nav(target);
  };
  const onRowKey = (id: string) => (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      openEdit(id);
    }
  };

  // Ledger no longer overrides --fab-bottom locally — the global default
  // in index.css :root sits the FAB ~24px above the last card and the
  // main padding auto-derives from it. Keeping this override would
  // double-lift and open a dead-space gap under the last card.

  return (
    <div className="space-y-5">
      <PageHeader
        icon={ScrollText}
        title="Ledger"
        subtitle="Every entry, filterable & searchable"
        testid="ledger-header"
      />

      {/* KPI card — unified, formatted, disclaimer-adjacent. Grid ratios
          are slightly wider for the last two cells on mobile so
          "Entries" and its count never clip off the right edge at 375px.
          Font sizes shrink one step on the smallest viewports. */}
      <div className="bi-elevate p-4 sm:p-5" data-testid="ledger-kpi-card">
        <div className="grid grid-cols-[1.4fr_1fr_1fr] gap-3 sm:gap-4 items-end">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 font-heading font-bold mb-1">
              Total
            </div>
            <div
              data-testid="ledger-kpi-total"
              className="font-heading text-[24px] sm:text-[30px] md:text-[34px] font-extrabold tabular-nums leading-none truncate"
            >
              {formatMoney(total)}
            </div>
          </div>
          <div className="border-l border-white/[0.06] pl-3 sm:pl-4 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#FF9500] font-heading font-bold mb-1 inline-flex items-center gap-1">
              <Receipt className="w-3 h-3" />
              GST
            </div>
            <div
              data-testid="ledger-kpi-gst"
              className="font-heading text-xl sm:text-2xl font-extrabold text-[#FF9500] tabular-nums leading-none truncate"
            >
              {formatMoney(gst)}
            </div>
          </div>
          <div className="border-l border-white/[0.06] pl-3 sm:pl-4 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/45 font-heading font-bold mb-1">
              Entries
            </div>
            <div
              data-testid="ledger-kpi-count"
              className="font-heading text-xl sm:text-2xl font-extrabold tabular-nums leading-none"
            >
              {filtered.length}
            </div>
          </div>
        </div>
      </div>

      <AtoDisclaimer context="tax_summary" compact testId="ledger-disclaimer" />

      {/* Search ----------------------------------------------------------- */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          data-testid="history-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search supplier, description…"
          className="w-full bi-input pl-10 h-12"
        />
      </div>

      {/* Filter pills ----------------------------------------------------- */}
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`history-filter-${s}`}
            onClick={() => setStatus(s)}
            aria-pressed={status === s}
            className={`h-10 px-4 rounded-full text-[11px] uppercase tracking-widest transition font-heading font-bold whitespace-nowrap ${
              status === s
                ? "bg-[#FF9500] text-white shadow-[0_6px_16px_-6px_rgba(255,149,0,0.55)]"
                : "bi-ghost-btn"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Deep-link flag filter chip — driven by ?flag=KEY (used by the
          assistant's "Show these" jumps). Advisory + clearable. */}
      {flagFilter && (
        <div
          data-testid="history-flag-filter-chip"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FBBF24]/12 border border-[#FBBF24]/35 text-[11px] text-[#FBBF24] font-heading font-bold uppercase tracking-wider"
        >
          Filtered · {({
            no_receipt: "Missing receipt",
            missing_abn_subcontractor: "Subbie missing ABN",
            gst_mismatch: "GST mismatch",
            unassigned_job: "Unassigned job",
          } as Record<string, string>)[flagFilter] || flagFilter}
          <span className="text-white/50 normal-case tracking-normal">
            · {(entriesByFlagKey[flagFilter]?.size ?? 0)} row{(entriesByFlagKey[flagFilter]?.size ?? 0) === 1 ? "" : "s"}
          </span>
          <button
            type="button"
            data-testid="history-flag-filter-clear"
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete("flag");
              setSearchParams(next, { replace: true });
            }}
            className="ml-1 text-[#FBBF24] hover:text-white transition"
            aria-label="Clear flag filter"
          >
            ✕
          </button>
        </div>
      )}

      {loading && (
        <div data-testid="ledger-loading" className="text-white/50 py-8 text-center">
          Loading…
        </div>
      )}
      {!loading && filtered.length === 0 && (
        <div
          data-testid="ledger-empty"
          className="bi-surface p-8 text-center text-white/60 space-y-3"
        >
          {items.length === 0 ? (
            <p className="text-sm">
              Your ledger is empty. Snap a receipt or speak an expense on the
              Capture screen to get started.
            </p>
          ) : (
            <>
              <p className="text-sm">
                {status !== "all" && q.trim().length > 0 ? (
                  <>
                    No entries match the{" "}
                    <b className="text-white/90 uppercase">{status}</b>{" "}
                    filter and search &ldquo;{q}&rdquo;.
                  </>
                ) : status !== "all" ? (
                  <>
                    No{" "}
                    <b className="text-white/90 uppercase">{status}</b>{" "}
                    entries yet.{" "}
                    <span className="text-white/50">
                      You have {items.length}{" "}
                      {items.length === 1 ? "entry" : "entries"} in other
                      filters.
                    </span>
                  </>
                ) : (
                  <>No entries match the search &ldquo;{q}&rdquo;.</>
                )}
              </p>
              {(status !== "all" || q.trim().length > 0) && (
                <button
                  type="button"
                  data-testid="ledger-empty-reset"
                  onClick={() => {
                    setStatus("all");
                    setQ("");
                  }}
                  className="h-10 px-4 rounded-xl bi-ghost-btn text-xs font-heading font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:brightness-125 transition"
                >
                  Reset filters
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Rows ------------------------------------------------------------- */}
      <div className="space-y-2">
        {filtered.map((e) => (
          <div
            key={e.id}
            data-testid={`history-item-${e.id}`}
            onClick={() => openEdit(e.id)}
            role="button"
            tabIndex={0}
            onKeyDown={onRowKey(e.id)}
            className="bi-surface p-3.5 cursor-pointer hover:bg-white/[0.03] active:scale-[0.995] transition"
          >
            <div className="flex gap-3">
              {/* Thumbnail — falls back to first image attachment so bulk
                  uploads (via AttachmentsPanel) still show evidence. */}
              {(() => {
                const attachThumb = (e.attachments || []).find(
                  (a) => (a.content_type || "").startsWith("image/")
                );
                const thumbPath = e.photo_path || attachThumb?.path;
                if (!thumbPath) {
                  return (
                    <div className="shrink-0 w-16 h-16 rounded-lg bg-white/5 border border-white/5 flex items-center justify-center text-white/30">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  );
                }
                return (
                  <a
                    data-testid={`history-photo-${e.id}`}
                    href={buildPhotoHref({
                      path: thumbPath,
                      token: fileToken,
                      supplier: e.supplier || "",
                      name: e.entry_date || "",
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(ev) => ev.stopPropagation()}
                    className="shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-black/40 border border-white/5 relative"
                  >
                    <img
                      src={`${API_BASE}/api/files/${thumbPath}`}
                      alt="receipt"
                      className="w-full h-full object-cover"
                    />
                    {(e.attachments?.length || 0) > 1 && (
                      <span
                        className="absolute bottom-0.5 right-0.5 text-[9px] px-1.5 py-0.5 rounded-md bg-black/70 text-white font-heading font-bold leading-none"
                        aria-label={`${e.attachments?.length} attachments`}
                      >
                        +{(e.attachments?.length || 1) - 1}
                      </span>
                    )}
                  </a>
                );
              })()}

              {/* Body — restructured into 5-6 tight rows so the amount is
                  never squeezed and GST never lands on the Confirmed chip.
                  Row 1: supplier + amount (amount is right-aligned inside
                    the body, sibling to supplier, so it stays visible even
                    when the kebab column is present).
                  Row 2: description (optional).
                  Row 3: date · GST (their own line — no chips, no job).
                  Row 4: job / category truncated on a single line — never
                    stacked into GST as "Un…" / "Re…".
                  Row 5: status chips (Confirmed / Status / flag / files).
                  Row 6: voice memo full-width (optional). */}
              <div className="flex-1 min-w-0 space-y-1.5">
                {/* Row 1 — supplier + amount */}
                <div className="flex items-baseline gap-2">
                  <div className="font-heading font-extrabold text-white truncate flex-1 min-w-0">
                    {e.supplier || "Unknown supplier"}
                  </div>
                  <div
                    data-testid={`history-amount-${e.id}`}
                    className="font-heading text-lg font-extrabold tabular-nums shrink-0 text-right"
                  >
                    {formatMoney(Number(e.total || 0))}
                  </div>
                </div>

                {/* Row 2 — description */}
                {e.description && (
                  <div className="text-xs text-white/60 truncate">
                    {e.description}
                  </div>
                )}

                {/* Row 3 — date · GST (own line, never sharing with chips) */}
                <div className="text-[11px] text-white/45 flex items-center gap-2">
                  <span className="tabular-nums">{formatDateShort(e.entry_date || "")}</span>
                  <span className="text-white/25">·</span>
                  <span data-testid={`history-gst-${e.id}`} className="tabular-nums">
                    GST {formatMoney(Number(e.gst || 0))}
                  </span>
                </div>

                {/* Row 4 — job / category on a single truncated line */}
                <div
                  data-testid={`history-job-${e.id}`}
                  className="text-[11px] text-white/55 truncate"
                >
                  {jobName(e.job_id)}
                </div>

                {/* Row 5 — status chips row (own row, cannot overlap GST) */}
                <div
                  data-testid={`history-chips-${e.id}`}
                  className="flex flex-wrap items-center gap-1.5 pt-0.5"
                >
                  {e.client_confirmed && (
                    <span
                      data-testid={`history-confirmed-${e.id}`}
                      title="You've vouched for the category and GST on this entry."
                      className="text-[10px] uppercase font-heading font-bold px-2 py-0.5 rounded-full border tracking-wider inline-flex items-center gap-1 border-[#FF9500]/70 text-[#FF9500] bg-[#FF9500]/[0.14]"
                    >
                      <CheckCircle2 className="w-3 h-3" /> Confirmed
                    </span>
                  )}
                  <span
                    data-testid={`history-status-${e.id}`}
                    className={`text-[10px] uppercase font-heading font-bold px-2 py-0.5 rounded-full border tracking-wider ${statusClass(e.status)}`}
                  >
                    {e.status}
                  </span>
                  <FlagPill count={flagsByEntry[e.id] || 0} testid={`history-flag-pill-${e.id}`} />
                  <AttachmentBadges entry={e} />
                </div>

                {/* Row 6 — voice memo full width under the chips. The
                    parent onClick opens edit, so we stop bubbling here
                    to let tap-to-play stay independent. */}
                {e.voice_path && (
                  <div
                    className="pt-1"
                    onClick={(ev) => ev.stopPropagation()}
                  >
                    <VoicePlayer
                      voicePath={e.voice_path}
                      transcript={e.raw_transcript}
                      testid={`history-voice-${e.id}`}
                    />
                  </div>
                )}
              </div>

              {/* Kebab column — 44×44 tap target, top-right, sibling of
                  the body so it never gets pushed onto the GST row. */}
              <div
                className="shrink-0 -mt-1 -mr-1"
                onClick={(ev) => ev.stopPropagation()}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid={`history-menu-${e.id}`}
                      aria-label="Row actions"
                      className="w-11 h-11 rounded-lg bi-ghost-btn flex items-center justify-center text-white/60 hover:text-white active:scale-95 transition"
                    >
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    data-testid={`history-menu-content-${e.id}`}
                    className="bg-popover border-white/10 text-white shadow-2xl"
                  >
                    <DropdownMenuItem
                      onClick={() => openEdit(e.id)}
                      data-testid={`history-menu-edit-${e.id}`}
                      className="cursor-pointer focus:bg-white/[0.06]"
                    >
                      <ChevronRight className="w-4 h-4 mr-2 text-[#FF9500]" />
                      Open / Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        // Radix closes the menu on this event's default,
                        // which is what we want. Defer the sheet open by
                        // a tick so the DropdownMenu's focus-return has
                        // finished before the Sheet's focus-trap engages
                        // — avoids a race where the sheet snaps closed.
                        setTimeout(
                          () => setExplainFor({ entryId: e.id, supplier: e.supplier }),
                          0
                        );
                      }}
                      data-testid={`history-menu-explain-${e.id}`}
                      className="cursor-pointer focus:bg-white/[0.06]"
                    >
                      <Sparkles className="w-4 h-4 mr-2 text-[#FF9500]" />
                      Explain
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        // Same deferral trick as Explain — let the menu
                        // dismiss cleanly before the OS share sheet /
                        // download fires, otherwise iOS Safari suppresses
                        // the share dialog as a "user activation lost".
                        setTimeout(() => shareReceipt(e), 0);
                      }}
                      data-testid={`history-menu-share-${e.id}`}
                      className="cursor-pointer focus:bg-white/[0.06]"
                    >
                      <Share2 className="w-4 h-4 mr-2 text-[#FF9500]" />
                      Share receipt
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-white/[0.06]" />
                    <DropdownMenuItem
                      onClick={() => del(e.id)}
                      data-testid={`history-menu-delete-${e.id}`}
                      className="cursor-pointer text-[#F87171] focus:bg-[#F87171]/10 focus:text-[#F87171]"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete entry
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Backwards-compat testids — kept so iter34 tests still resolve. */}
            <button
              aria-hidden
              tabIndex={-1}
              onClick={(ev) => { ev.stopPropagation(); del(e.id); }}
              data-testid={`history-del-${e.id}`}
              className="sr-only"
            >
              legacy-delete
            </button>
          </div>
        ))}
      </div>

      {/* Ledger footer — surfaces which ATO ruleset stamped the GST /
          category totals shown above. */}
      <div className="text-center pt-2" data-testid="history-ruleset-footer">
        <RulesetStamp />
      </div>

      {/* Single, page-level Explain sheet — driven by the row DropdownMenu's
          onSelect. Lifting the sheet state out of the row solves the Radix
          `asChild + inner-button` interaction bug where clicks were being
          swallowed by the menu-item's own onSelect handler. */}
      <ExplainEntryButton
        hideTrigger
        open={explainFor !== null}
        onOpenChange={(o) => {
          if (!o) setExplainFor(null);
        }}
        entryId={explainFor?.entryId}
        supplier={explainFor?.supplier}
      />

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        title="Delete this entry?"
        description="This can't be undone. The entry and its attachments will be removed from the ledger."
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
        testId="ledger-delete"
      />
      {/* Trailing clearance so the last ledger row + inline voice player
          are never covered by the Assistant FAB or the tab bar. Matches
          the pattern used on Tax + Export. */}
      <div aria-hidden className="h-24" data-testid="ledger-fab-clearance" />
    </div>
  );
}
